// AutomationRuleActions.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Utilities
import {
  buildRecipientModalPeopleValue,
  getActionType,
  normalizeRecipientSource,
  resolveExplicitRecipientProfiles,
} from './utils/automationActionEditorUtils';
import { peopleLiteralToUserKeys } from 'collection/automations/utils/automationValueUtils';

// Services
import { subscribeToBaseMembers } from 'services/bases/BaseMemberService';

// Hooks
import useAutomationRuleActionsState from './hooks/useAutomationRuleActionsState';

// CSS
import './AutomationRuleActions.css';

// Components
import SendEmailActionRow from './components/SendEmailActionRow';
import SetFieldActionRow from './components/SetFieldActionRow';
import MemberPickerModal from 'components/memberpicker/MemberPickerModal';

/**
 * AutomationRuleActions
 *
 * Renders editable action rows for an automation rule.
 *
 * @param {object} rule - The parent rule containing action rows.
 * @param {array} actions - Action rows to render.
 * @param {object} theme - Theme object used for dynamic styling.
 * @param {array} fieldOptions - Available fields for action field selection.
 * @param {Map} fieldTypeByKey - Field type lookup keyed by field key.
 * @param {Map} fieldEnumOptionsByKey - Enum option lookup keyed by field key.
 * @param {string} baseKey - Active base key for member recipient selection.
 * @param {array} nodeTypeOptions - Base node types with people field options.
 * @param {boolean} [allowEmailActions=true] - Whether send-email actions can be selected.
 * @param {function} updateRule - Updates a rule by key with an updater callback.
 * @param {function} handleRemoveAction - Removes an action row from a rule.
 *
 * @returns {ReactNode} The rendered action rows.
 */
const AutomationRuleActions = React.memo(({
  rule,
  actions,
  theme,
  fieldOptions,
  fieldTypeByKey,
  fieldEnumOptionsByKey,
  baseKey,
  nodeTypeOptions,
  allowEmailActions = true,
  updateRule,
  handleRemoveAction,
}) => {

  // ----- STATE ----- //

  const [baseMembers, setBaseMembers] = useState([]);

  // ----- HOOKS ----- //

  const {
    actionState,
    actionHandlers,
  } = useAutomationRuleActionsState({
    rule,
    actions,
    fieldTypeByKey,
    allowEmailActions,
    updateRule,
  });

  const {
    normalizedActions,
    recipientModalActionKey,
    recipientModalAction,
  } = actionState;
  const {
    updateAction,
    handleActionTypeChange,
    handleRecipientModalOpen,
    handleRecipientModalClose,
    handleRecipientChange,
  } = actionHandlers;

  // ----- EFFECTS ----- //

  useEffect(() => {
    const cleanBaseKey = String(baseKey || '').trim();
    if (!cleanBaseKey) {
      setBaseMembers([]);
      return undefined;
    }

    const unsubscribe = subscribeToBaseMembers(
      cleanBaseKey,
      (rows) => {
        setBaseMembers(Array.isArray(rows) ? rows : []);
      },
      () => {
        setBaseMembers([]);
      },
    );

    return () => {
      unsubscribe?.();
    };
  }, [baseKey]);

  // ----- MEMOS ----- //

  const selectedRecipientsByActionKey = useMemo(() => {
    const recipientsByActionKey = new Map();
    normalizedActions.forEach((action) => {
      if (getActionType(action) !== 'send_email') return;
      const recipientSource = normalizeRecipientSource(action);
      recipientsByActionKey.set(
        action.key,
        resolveExplicitRecipientProfiles(
          recipientSource.explicitMemberUserKeys,
          baseMembers,
        ),
      );
    });
    return recipientsByActionKey;
  }, [normalizedActions, baseMembers]);

  // ----- RENDER ----- //

  return (
    <div className="automation-rule-actions-container">
      {normalizedActions.map((action) => {
        const actionType = getActionType(action);

        if (actionType === 'send_email') {
          return (
            <SendEmailActionRow
              key={action.key}
              action={action}
              theme={theme}
              actions={normalizedActions}
              fieldOptions={fieldOptions}
              nodeTypeOptions={nodeTypeOptions}
              allowEmailActions={allowEmailActions}
              selectedRecipients={selectedRecipientsByActionKey.get(action.key) || []}
              updateAction={(updater) => updateAction(action.key, updater)}
              onOpenRecipients={() => handleRecipientModalOpen(action.key)}
              onRemove={() => handleRemoveAction(rule.key, action.key)}
              onTypeChange={(nextType) => {
                handleActionTypeChange(action.key, nextType, { preserveSetFieldState: false });
              }}
            />
          );
        }

        return (
          <SetFieldActionRow
            key={action.key}
            action={action}
            theme={theme}
            actions={normalizedActions}
            fieldOptions={fieldOptions}
            fieldTypeByKey={fieldTypeByKey}
            fieldEnumOptionsByKey={fieldEnumOptionsByKey}
            baseMembers={baseMembers}
            allowEmailActions={allowEmailActions}
            updateAction={(updater) => updateAction(action.key, updater)}
            onRemove={() => handleRemoveAction(rule.key, action.key)}
            onOpenPeoplePicker={() => handleRecipientModalOpen(action.key)}
            onTypeChange={(nextType) => {
              handleActionTypeChange(action.key, nextType, { preserveSetFieldState: true });
            }}
          />
        );
      })}

      {recipientModalAction ? (
        <MemberPickerModal
          baseKey={baseKey}
          label={
            getActionType(recipientModalAction) === 'send_email'
              ? 'Select recipients'
              : 'Select people'
          }
          value={
            getActionType(recipientModalAction) === 'send_email'
              ? buildRecipientModalPeopleValue(recipientModalAction)
              : resolveExplicitRecipientProfiles(
                peopleLiteralToUserKeys(recipientModalAction?.value?.literal),
                baseMembers,
              ).map((person) => ({
                key: person.userKey,
                userKey: person.userKey,
                title: person.name,
                coverUrl: person.coverUrl,
              }))
          }
          isOpen={Boolean(recipientModalActionKey)}
          setOpen={(open) => {
            if (!open) handleRecipientModalClose();
          }}
          onChange={handleRecipientChange}
        />
      ) : null}
    </div>
  );
});

// Dev Tools
AutomationRuleActions.displayName = 'AutomationRuleActions';

export default AutomationRuleActions;
